import React, { useState, useRef, useEffect } from 'react';

import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const Biography: React.FC = () => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageWidth, setPageWidth] = useState<number>(800);
  const [language, setLanguage] = useState<'en' | 'te'>('en');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const isMobile = window.innerWidth < 768;

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1); // Reset to page 1 on language change
  };

  useEffect(() => {
    const updatePageWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setPageWidth(Math.min(width, 800));
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isMobile) {
        if (e.key === 'ArrowRight') {
          setPageNumber((prev) => (prev < (numPages ?? 1) ? prev + 1 : prev));
        } else if (e.key === 'ArrowLeft') {
          setPageNumber((prev) => (prev > 1 ? prev - 1 : prev));
        }
      }
    };

    updatePageWidth();
    window.addEventListener('resize', updatePageWidth);
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('resize', updatePageWidth);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [numPages, isMobile]);

  const fileUrl =
    language === 'en'
      ? 'https://thinksabio-asset.s3.us-east-1.amazonaws.com/Biography.pdf'
      : 'https://thinksabio-asset.s3.us-east-1.amazonaws.com/Biography_Telugu.pdf';

  return (
    <div
      style={{
        height: '100vh',
        overflowY: 'auto',
        padding: '1rem',
        marginTop: '80px',
        boxSizing: 'border-box',
      }}
    >
      {/* Language Switch */}
      <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
        <button
          onClick={() => setLanguage('en')}
          style={{
            padding: '0.5rem 1rem',
            marginRight: '1rem',
            backgroundColor: language === 'en' ? '#3b82f6' : '#e5e7eb',
            color: language === 'en' ? 'white' : 'black',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
          }}
        >
          English
        </button>
        <button
          onClick={() => setLanguage('te')}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: language === 'te' ? '#3b82f6' : '#e5e7eb',
            color: language === 'te' ? 'white' : 'black',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
          }}
        >
          తెలుగు
        </button>
      </div>

      {/* PDF Viewer */}
      <div
        ref={containerRef}
        style={{
          maxWidth: '900px',
          margin: '0 auto',
          width: '100%',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Document
          file={fileUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={<p>Loading PDF...</p>}
        >
          {isMobile ? (
            Array.from(new Array(numPages), (_, index) => (
              <div
                key={`page_${index + 1}`}
                style={{
                  marginBottom: '40px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  backgroundColor: 'white',
                }}
              >
                <Page
                  pageNumber={index + 1}
                  width={pageWidth}
                  renderTextLayer={false}
                />
              </div>
            ))
          ) : (
            <Page
              pageNumber={pageNumber}
              width={pageWidth}
              renderTextLayer={false}
            />
          )}
        </Document>

        {/* Navigation (Desktop only) */}
        {!isMobile && (
          <>
            {/* Left Arrow */}
            <button
              onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
              disabled={pageNumber === 1}
              style={{
                position: 'absolute',
                left: '-40px',
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: '#374151',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                cursor: pageNumber === 1 ? 'not-allowed' : 'pointer',
                color: 'white',
                fontSize: '20px',
                fontWeight: 'bold',
                boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
              }}
            >
              ←
            </button>

            {/* Right Arrow */}
            <button
              onClick={() =>
                setPageNumber((prev) =>
                  prev < (numPages ?? 1) ? prev + 1 : prev,
                )
              }
              disabled={pageNumber === numPages}
              style={{
                position: 'absolute',
                right: '-40px',
                top: '50%',
                transform: 'translateY(-50%)',
                backgroundColor: '#374151',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                cursor: pageNumber === numPages ? 'not-allowed' : 'pointer',
                color: 'white',
                fontSize: '20px',
                fontWeight: 'bold',
                boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
              }}
            >
              →
            </button>

            {/* Page Info */}
            <div
              style={{
                marginTop: '1.2rem',
                color: '#d1d5db',
                fontSize: '14px',
                fontFamily: 'sans-serif',
              }}
            >
              Page <strong>{pageNumber}</strong> of <strong>{numPages}</strong>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Biography;
